import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';
import WebPageLayout from '../../../components/layout/WebPageLayout';
import mainLogo from '../../../assets/Logo/main_logo.png';
import adBannerWeb from '../../../assets/Icon/project/ad_banner_web.png';
import engBannerWeb from '../../../assets/Icon/project/Eng_Web.png';
import backgroundImage from '../../../assets/img/background.png';
import deleteIcon from '../../../assets/Icon/project/delete_icno.png';
import { PATHS } from '../../../routes/paths';
import { useDonationHistoryFind } from '../useDonationHistoryFind';
import {
  MISSIONFUND_PRIVACY_POLICY_URL,
  MISSIONFUND_TERMS_OF_SERVICE_URL,
} from '../../../Utils/missionFundLegalUrls';

const DonationHistoryFindWeb = () => {
  const { i18n, t } = useTranslation('translation');
  const navigate = useNavigate();
  const currentLang = (i18n.resolvedLanguage || i18n.language || 'ko').split('-')[0];
  const [isEmailFocused, setIsEmailFocused] = useState(false);
  const {
    email,
    setEmail,
    error,
    isSubmitting,
    isSubmitDisabled,
    isSuccessModalOpen,
    sentEmail,
    closeSuccessModal,
    handleSubmit,
  } = useDonationHistoryFind();
  const hasEmailValue = useMemo(() => Boolean(email.trim()), [email]);
  const shouldShowEmailLabel = isEmailFocused || hasEmailValue;
  const bannerImage = currentLang === 'en' ? engBannerWeb : adBannerWeb;
  const copy = useMemo(() => ({
    title: t('page.donationHistoryFind.title'),
    descriptionLine1: t('page.donationHistoryFind.descriptionLine1'),
    descriptionLine2: t('page.donationHistoryFind.descriptionLine2'),
    submit: t('page.donationHistoryFind.actions.send'),
    submitting: t('page.donationHistoryFind.actions.sending'),
    modalDescriptionLine1: t('page.donationHistoryFind.successModal.descriptionLine1'),
    modalDescriptionLine2: t('page.donationHistoryFind.successModal.descriptionLineWeb'),
    modalBack: t('page.donationHistoryFind.successModal.back'),
  }), [t]);

  const handleBack = () => {
    closeSuccessModal();
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate(PATHS.LOGIN);
  };

  return (
    <WebPageLayout title="" hideHeader hideFooter>
      <BackgroundOverlay />
      <Centered>
        <Card>
          <HeaderRow>
            <LangSelect value={currentLang} onChange={(e) => i18n.changeLanguage(e.target.value)}>
              <option value="ko">{t('lang.ko')}</option>
              <option value="en">{t('lang.en')}</option>
            </LangSelect>
          </HeaderRow>

          <LogoSection>
            <LogoLink to={PATHS.LOGIN}>
              <Logo src={mainLogo} alt="Mission Fund" />
            </LogoLink>
          </LogoSection>

          <Title>{copy.title}</Title>
          <Description>
            {copy.descriptionLine1}
            <br />
            {copy.descriptionLine2}
          </Description>

          <Form onSubmit={handleSubmit}>
            <InputBlock>
              <EmailInputBox $hasError={Boolean(error)} $active={shouldShowEmailLabel}>
                {shouldShowEmailLabel ? (
                  <EmailInputLabel>{t('page.passwordFind.emailPlaceholder')}</EmailInputLabel>
                ) : null}
                <EmailInputRow>
                  <EmailInput
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onFocus={() => setIsEmailFocused(true)}
                    onBlur={() => setIsEmailFocused(false)}
                    placeholder={shouldShowEmailLabel ? '' : t('page.passwordFind.emailPlaceholder')}
                    autoComplete="email"
                  />
                  {hasEmailValue ? (
                    <ClearButton
                      type="button"
                      aria-label="clear email"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => setEmail('')}
                    >
                      <ClearButtonIcon src={deleteIcon} alt="clear" />
                    </ClearButton>
                  ) : null}
                </EmailInputRow>
              </EmailInputBox>
              {error ? <ErrorText>{error}</ErrorText> : null}
            </InputBlock>

            <SubmitButton type="submit" disabled={isSubmitDisabled}>
              {isSubmitting ? copy.submitting : copy.submit}
            </SubmitButton>
          </Form>
          <BannerSection href="https://missionfund.org/support/" target="_blank" rel="noopener noreferrer">
            <BannerImage src={bannerImage} alt={t('page.login.bannerAlt')} />
          </BannerSection>

          <FooterLinks>
            <FooterLegalLink
              href={MISSIONFUND_TERMS_OF_SERVICE_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('common.footer.servicePolicy')}
            </FooterLegalLink>
            <FooterDivider>|</FooterDivider>
            <FooterLegalLink
              href={MISSIONFUND_PRIVACY_POLICY_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('common.footer.privacyPolicy')}
            </FooterLegalLink>
          </FooterLinks>
        </Card>
      </Centered>
      {isSuccessModalOpen ? (
        <SuccessModalOverlay>
          <SuccessModalCard>
            <SuccessModalTitle>{copy.title}</SuccessModalTitle>
            <SuccessModalEmail>{sentEmail}</SuccessModalEmail>
            <SuccessModalDescription>
              {copy.modalDescriptionLine1}
              <br />
              {copy.modalDescriptionLine2}
            </SuccessModalDescription>
            <SuccessModalButton type="button" onClick={handleBack}>
              {copy.modalBack}
            </SuccessModalButton>
          </SuccessModalCard>
        </SuccessModalOverlay>
      ) : null}
    </WebPageLayout>
  );
};

const BackgroundOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: url(${backgroundImage});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  z-index: 0;
  pointer-events: none;
`;

const Centered = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 60px 0;
  width: 100%;
  position: relative;
  z-index: 1;
`;

const Card = styled.div`
  width: 560px;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
`;

const HeaderRow = styled.div`
  position: absolute;
  top: -55px;
  right: -179px;
  z-index: 10;
`;

const LangSelect = styled.select`
  width: auto;
  min-width: 118px;
  height: 40px;
  padding: 8px 36px 8px 12px;
  border-radius: 4px;
  border: 1px solid #bcbcbc;
  background: #ffffff;
  color: #676767;
  font-family: Pretendard, sans-serif;
  font-size: 20px;
  font-weight: 500;
  cursor: pointer;
  outline: none;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg width='14' height='8' viewBox='0 0 14 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L7 7L13 1' stroke='%23BCBCBC' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 14px 8px;
`;

const LogoSection = styled.div`
  width: 272px;
  height: 85px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 20px;
  margin-bottom: 20px;
`;

const LogoLink = styled(Link)`
  display: block;
  cursor: pointer;
  text-decoration: none;
`;

const Logo = styled.img`
  width: 252px;
  height: 65px;
  object-fit: contain;
`;

const Title = styled.h1`
  margin: 0 0 16px;
  color: #17171b;
  text-align: center;
  font-family: Pretendard, sans-serif;
  font-size: 24px;
  font-weight: 600;
  line-height: 120%;
`;

const Description = styled.p`
  margin: 0;
  color: #17171b;
  text-align: center;
  font-family: Pretendard, sans-serif;
  font-size: 20px;
  font-weight: 500;
  line-height: 150%;
`;

const Form = styled.form`
  width: 100%;
  max-width: 360px;
  display: flex;
  flex-direction: column;
  gap: 36px;
  margin-top: 24px;
`;

const InputBlock = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const EmailInputBox = styled.div<{ $hasError?: boolean; $active?: boolean }>`
  width: 100%;
  min-height: ${({ $active }) => ($active ? '74px' : '58px')};
  border-radius: 4px;
  border: ${({ $hasError, $active }) =>
    $hasError ? '1.6px solid #f00' : $active ? '1.6px solid #2bc2e1' : '1px solid #eeeeee'};
  background: #ffffff;
  padding: 8px 12px;
  display: flex;
  flex-direction: column;
  justify-content: ${({ $active }) => ($active ? 'flex-start' : 'center')};
  gap: ${({ $active }) => ($active ? '2px' : '0')};
`;

const EmailInputLabel = styled.label`
  color: #929292;
  font-family: Pretendard, sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 150%;
`;

const EmailInputRow = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const EmailInput = styled.input`
  flex: 1;
  min-width: 0;
  width: 100%;
  border: none;
  background: transparent;
  padding: 0;
  color: #17171b;
  font-family: Pretendard, sans-serif;
  font-size: 24px;
  font-weight: 400;
  line-height: 150%;
  outline: none;

  &::placeholder {
    color: #929292;
  }
`;

const ClearButton = styled.button`
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const ClearButtonIcon = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const SubmitButton = styled.button`
  height: 60px;
  padding: 10px 35.5px;
  border: none;
  border-radius: 12px;
  background: ${({ disabled }) => (disabled ? '#d6d7da' : '#026478')};
  color: ${({ disabled }) => (disabled ? '#BCBCBC' : '#ffffff')};
  font-family: Pretendard, sans-serif;
  font-size: 20px;
  font-weight: 400;
  line-height: 150%;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
`;

const ErrorText = styled.p`
  width: 100%;
  margin: 0;
  color: #f00;
  font-family: Pretendard, sans-serif;
  font-size: 20px;
  font-weight: 400;
  line-height: 150%;
  text-align: left;
`;

const BannerSection = styled.a`
  width: 120%;
  cursor: pointer;
  transition: transform 0.2s;
  margin-top: 104px;
`;

const BannerImage = styled.img`
  width: 100%;
  height: auto;
  display: block;
`;

const FooterLinks = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
  width: 100%;
`;

const FooterLegalLink = styled.a`
  color: #676767;
  font-family: Pretendard, sans-serif;
  font-size: 16px;
  font-weight: 500;
  line-height: 100%;
  cursor: pointer;
  text-decoration: none;

  &:hover {
    color: #17171b;
    text-decoration: underline;
  }
`;

const FooterDivider = styled.span`
  font-family: Pretendard, sans-serif;
  font-size: 24px;
  font-weight: 300;
  color: #BCBCBC;
`;

const SuccessModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(23, 23, 27, 0.42);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 24px;
`;

const SuccessModalCard = styled.div`
  display: flex;
  width: min(480px, calc(100vw - 40px));
  height: 347px;
  padding: 44px 28px 28px 28px;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  border-radius: 20px;
  background: #fff;
  box-shadow: 0 0 12px 0 rgba(0, 0, 0, 0.35);
`;

const SuccessModalTitle = styled.h2`
  margin: 0 0 44px;
  color: #17171b;
  text-align: center;
  font-family: Pretendard, sans-serif;
  font-size: 24px;
  font-weight: 600;
  line-height: 120%;
`;

const SuccessModalEmail = styled.p`
  margin: 0 0 16px;
  color: #17171b;
  text-align: center;
  font-family: Pretendard, sans-serif;
  font-size: 24px;
  font-weight: 600;
  line-height: 120%;
`;

const SuccessModalDescription = styled.p`
  margin: 0 0 43px;
  color: #17171b;
  text-align: center;
  font-family: Pretendard, sans-serif;
  font-size: 20px;
  font-weight: 500;
  line-height: 150%;
`;

const SuccessModalButton = styled.button`
  margin-top: 0;
  width: 100%;
  height: 58px;
  border: none;
  border-radius: 12px;
  background: #026478;
  color: #fff;
  text-align: center;
  font-family: Pretendard, sans-serif;
  font-size: 20px;
  font-weight: 400;
  line-height: 150%;
  cursor: pointer;
`;

export default DonationHistoryFindWeb;

